import type { ArtistProfileResponse } from 'fym-shared/src/music24/artistProfile';
import {
  type ProfileSendFailCode,
  type ProfileSendFileRefusal,
  type ProfileSendItem,
  type ProfileSendItemAvailability,
  type ProfileSendOverviewResponse,
  type ProfileSendService,
  type ProfileSendServiceAvailability,
  type ProfileSendTarget,
  type ProfileSendUnavailable,
  type SerializedProfileSendItem,
} from 'fym-shared/src/music24/profileSend';

/**
 * The rules of the "Send to all" sheet (design profile-card c1–c6). Pure: the sheet parts only
 * draw what these functions give.
 */

export type SendAllChipKind = 'live' | 'sending' | 'waiting' | 'failed' | 'readOnly';

export interface SendAllChip {
  kind: SendAllChipKind;
  /** `live`: the time of the send that the service took. */
  at: string | null;
}

/** What the row of a failed item offers: Retry, "Sign in again", or nothing (noPermission, read-only). */
export type SendAllRetry = 'retry' | 'reconnect' | null;

export interface SendAllItemView {
  key: string;
  service: ProfileSendService;
  item: ProfileSendItem;
  /** False: the tick box is off (it cannot go now, or the service holds its own text). */
  canTick: boolean;
  readOnly: boolean;
  chip: SendAllChip | null;
  unavailable: ProfileSendUnavailable | null;
  fileRefusal: ProfileSendFileRefusal | null;
  failCode: ProfileSendFailCode | null;
  failDetail: string | null;
  retry: SendAllRetry;
  /** Live, and the version (or the bio) on the service is the one that would go now. */
  isCurrent: boolean;
  /** Queued or sending now. */
  inFlight: boolean;
}

export interface SendAllServiceView {
  service: ProfileSendService;
  connected: boolean;
  accountName: string | null;
  /** "Coming soon" (Facebook while the app review is open). */
  comingSoon: boolean;
  /** The action of the head row. */
  action: 'connect' | 'reconnect' | null;
  /** The service cannot send at all: the items fold to the head row. */
  off: boolean;
  items: SendAllItemView[];
}

/** The key of one item in the tick state. Pure. */
export function itemKey(service: ProfileSendService, item: ProfileSendItem): string {
  return `${service}.${item}`;
}

function sentItemOf(
  overview: ProfileSendOverviewResponse,
  service: ProfileSendService,
  one: ProfileSendItemAvailability
): SerializedProfileSendItem | null {
  if (one.last === null) {
    return null;
  }
  const send = overview.recent.find((candidate) => candidate.uuid === one.last?.sendUuid);
  return send?.items.find((candidate) => candidate.service === service && candidate.item === one.item) ?? null;
}

function retryOf(failCode: ProfileSendFailCode | null): SendAllRetry {
  if (failCode === 'reconnect') {
    return 'reconnect';
  }
  if (failCode === 'noPermission' || failCode === 'readOnlyOnService') {
    return null;
  }
  return 'retry';
}

function isCurrentLive(one: ProfileSendItemAvailability, bioUpdatedAt: string | null): boolean {
  if (one.last?.status !== 'live') {
    return false;
  }
  if (one.item === 'bio') {
    return bioUpdatedAt === null || Date.parse(one.last.at) >= Date.parse(bioUpdatedAt);
  }
  if (one.item === 'facebookLink') {
    return true;
  }
  return one.last.assetUuid === one.assetUuid;
}

function itemView(
  overview: ProfileSendOverviewResponse,
  service: ProfileSendService,
  one: ProfileSendItemAvailability,
  bioUpdatedAt: string | null
): SendAllItemView {
  const sent = sentItemOf(overview, service, one);
  const status = one.last?.status ?? null;
  const failCode = status === 'failed' ? (sent?.failCode ?? 'error') : null;
  const readOnly = failCode === 'readOnlyOnService';
  const inFlight = status === 'queued' || status === 'sending' || one.unavailable === 'busy';
  let chip: SendAllChip | null = null;
  if (readOnly) {
    chip = { kind: 'readOnly', at: null };
  } else if (status === 'live') {
    chip = { kind: 'live', at: sent?.finishedAt ?? one.last?.at ?? null };
  } else if (status === 'sending' || one.unavailable === 'busy') {
    chip = { kind: 'sending', at: null };
  } else if (status === 'queued') {
    chip = { kind: 'waiting', at: null };
  } else if (status === 'failed') {
    chip = { kind: 'failed', at: null };
  }
  const canGo = one.unavailable === null || one.unavailable === 'busy';
  return {
    key: itemKey(service, one.item),
    service,
    item: one.item,
    canTick: one.unavailable === null && !readOnly,
    readOnly,
    chip,
    unavailable: one.unavailable,
    fileRefusal: one.fileRefusal,
    failCode,
    failDetail: status === 'failed' ? (sent?.failDetail ?? null) : null,
    retry: failCode !== null && canGo ? retryOf(failCode) : null,
    isCurrent: isCurrentLive(one, bioUpdatedAt),
    inFlight,
  };
}

function serviceView(
  overview: ProfileSendOverviewResponse,
  one: ProfileSendServiceAvailability,
  bioUpdatedAt: string | null
): SendAllServiceView {
  const items = one.items.map((item) => itemView(overview, one.service, item, bioUpdatedAt));
  const comingSoon = one.unavailable === 'notAvailableYet';
  let action: SendAllServiceView['action'] = null;
  if (!comingSoon && !one.connected) {
    action = 'connect';
  } else if (one.unavailable === 'reconnect' || one.unavailable === 'needsScope') {
    action = 'reconnect';
  }
  const off = comingSoon || !one.connected || one.unavailable === 'notConnected';
  return {
    service: one.service,
    connected: one.connected,
    accountName: one.accountName,
    comingSoon,
    action,
    off,
    items,
  };
}

/** The rows of the sheet, in the order of the API. Pure. */
export function sendAllServices(
  overview: ProfileSendOverviewResponse,
  profile: Pick<ArtistProfileResponse, 'bio'>
): SendAllServiceView[] {
  return overview.services.map((one) => serviceView(overview, one, profile.bio.updatedAt));
}

/**
 * The tick of an item: the stored choice, else on for each item that can go. With `only` (the
 * Spotify upload, `?sheet=spotify`) only the items of that service start ticked. Pure.
 */
export function isTicked(
  view: SendAllItemView,
  ticks: Readonly<Record<string, boolean>>,
  only: ProfileSendService | null = null
): boolean {
  if (!view.canTick) {
    return false;
  }
  return ticks[view.key] ?? (only === null || view.service === only);
}

function targetsOf(items: SendAllItemView[]): ProfileSendTarget[] {
  const out: ProfileSendTarget[] = [];
  items.forEach((one) => {
    const target = out.find((candidate) => candidate.service === one.service);
    if (target === undefined) {
      out.push({ service: one.service, items: [one.item] });
    } else {
      target.items.push(one.item);
    }
  });
  return out;
}

export type SendAllFooter =
  | { kind: 'working'; services: number }
  | { kind: 'send'; services: number; targets: ProfileSendTarget[] }
  | { kind: 'retry'; items: number; targets: ProfileSendTarget[] }
  | { kind: 'nothing'; services: number; reason: 'noService' | 'allLive' };

/**
 * The footer of the sheet (design c1–c5): a send that is on its way shows only Close; else
 * "Send to N services" (the ticked items that are not live and current; ticked failed items go
 * too), else "Retry N items" (only failed items wait), else an off button. Pure.
 */
export function sendAllFooter(
  services: SendAllServiceView[],
  ticks: Readonly<Record<string, boolean>>,
  isWorking: boolean,
  only: ProfileSendService | null = null
): SendAllFooter {
  const all = services.flatMap((one) => one.items);
  const ticked = all.filter((one) => isTicked(one, ticks, only));
  if (isWorking) {
    const busy = new Set(all.filter((one) => one.inFlight).map((one) => one.service));
    return { kind: 'working', services: busy.size };
  }
  const fresh = ticked.filter((one) => !one.isCurrent && one.failCode === null && !one.inFlight);
  const failed = all.filter((one) => one.retry === 'retry' && !one.inFlight && (only === null || one.service === only));
  if (fresh.length > 0) {
    const tickedFailed = failed.filter((one) => isTicked(one, ticks, only));
    const targets = targetsOf([...fresh, ...tickedFailed]);
    return { kind: 'send', services: targets.length, targets };
  }
  if (failed.length > 0) {
    return { kind: 'retry', items: failed.length, targets: targetsOf(failed) };
  }
  const tickedServices = new Set(ticked.map((one) => one.service)).size;
  return { kind: 'nothing', services: tickedServices, reason: tickedServices === 0 ? 'noService' : 'allLive' };
}

/** True while a send of the sheet is on its way. Pure. */
export function isSendWorking(overview: ProfileSendOverviewResponse): boolean {
  return overview.recent.some((send) => send.status === 'working');
}

/** The lead line of "We send it for you" (design c1–c5). Pure. */
export type SendAllLead = 'noneConnected' | 'pick' | 'working' | 'allLive' | 'someFailed';

export function sendAllLead(services: SendAllServiceView[], isWorking: boolean): SendAllLead {
  if (isWorking) {
    return 'working';
  }
  if (!services.some((one) => one.connected && !one.comingSoon)) {
    return 'noneConnected';
  }
  const items = services.flatMap((one) => one.items);
  if (items.some((one) => one.chip?.kind === 'failed')) {
    return 'someFailed';
  }
  const open = items.filter((one) => one.canTick);
  if (open.length > 0 && open.every((one) => one.isCurrent)) {
    return 'allLive';
  }
  return 'pick';
}

/** The count of failed items (the lead "1 item failed"). Pure. */
export function failedCount(services: SendAllServiceView[]): number {
  return services.flatMap((one) => one.items).filter((one) => one.chip?.kind === 'failed').length;
}

/** The targets of the Retry of one row. Pure. */
export function retryTargets(view: SendAllItemView): ProfileSendTarget[] {
  return [{ service: view.service, items: [view.item] }];
}
